import {
  BarRaiserStatus,
  ContentQualityResponseOptions,
  ContentRequestType,
  YearsOfExperience,
} from '@amzn/awscat-pattern-library-service-model';
import {
  AWSService,
  ContentAudience,
  ContentNodeType,
  ContentPriority,
  ContentRequestState,
  ContentSubType,
  ContentType,
  Environment,
  IndependentSoftwareVendor,
  LifeCycle,
  PatternAccreditation,
  PatternProvenance,
  PatternType,
  RType,
  TaskSkill,
  Technology,
  TechWriterStatus,
  WorkflowEventTypes,
  WorkflowState,
  Workload,
  PublicationIssueType,
  PublicationFeedbackData,
  PublicationFeedbackState,
  WorkflowTag,
} from '@amzn/awscat-pattern-library-service-model';
import { FinalizingGraphState, MetricType } from '../../model/Enum';
import { PatternCategory } from '../../model/PatternCategory';
import UserTitle from '../../model/UserTitle';

export type MetadataFieldType =
  | string
  | RType
  | PatternType
  | PatternType
  | LifeCycle
  | PatternAccreditation
  | PatternProvenance
  | IndependentSoftwareVendor
  | Technology;

export const displayMetadataNames = (
  ids: MetadataFieldType[],
  displayNames: Map<MetadataFieldType, string>,
) => {
  if (Array.isArray(ids) && ids.length) {
    return ids
      .map((id) => displayNames.get(id) || id)
      .filter((i) => i)
      .join(', ');
  }
  return '';
};

export const autoSuggestionContentTypeDisplayNames = new Map<ContentType, string>([
  [ContentType.Pattern, 'Patterns'],
  [ContentType.Guide, 'Guides'],
  [ContentType.Strategy, 'Strategies'],
]);

export const contentTypeDisplayNames = new Map<ContentType, string>([
  [ContentType.Pattern, 'Pattern'],
  [ContentType.Guide, 'Guide'],
  [ContentType.Strategy, 'Strategy'],
  [ContentType.ExternalResource, 'External Resource'],
]);

export const contentRequestTypeDisplayNames = new Map<ContentRequestType, string>([
  [ContentRequestType.Create, 'New Content'],
  [ContentRequestType.Update, 'Content needs update'],
]);

export const contentRequestStateDisplayNames = new Map<ContentRequestState, string>([
  [ContentRequestState.IN_REVIEW, 'In review'],
  [ContentRequestState.REQUESTED, 'Needs author'],
  [ContentRequestState.ASSIGNED, 'Pending acceptance'],
  [ContentRequestState.ACCEPTED, 'Accepted'],
  [ContentRequestState.IN_PROGRESS, 'In progress'],
  [ContentRequestState.REJECTED, 'Rejected'],
  [ContentRequestState.COMPLETED, 'Completed'],
  [ContentRequestState.DUPLICATED, 'Duplicated'],
]);

export const contentAudienceDisplayNames = new Map<ContentAudience, string>([
  [ContentAudience.INTERNAL, 'Internal'],
  [ContentAudience.EXTERNAL, 'AWS internal users and AWS Partners'],
  [ContentAudience.PUBLIC, 'External'],
]);

export const contentAudienceDetails = new Map<ContentAudience, { topics: string }>([
  [ContentAudience.INTERNAL, { topics: 'AWS employees only' }],
  [ContentAudience.PUBLIC, { topics: 'AWS employees and customers' }],
]);

export const contentAudienceBadgeDisplayNames = new Map<ContentAudience, string>([
  [ContentAudience.INTERNAL, 'AWS'],
  [ContentAudience.PARTNER, 'Partner'],
  [ContentAudience.EXTERNAL, 'AWS'],
  [ContentAudience.PUBLIC, 'Public'],
]);

// @ts-expect-error TS2769 - strict migration
export const workflowStateToAudienceMap = new Map<WorkflowState, string>([
  [WorkflowState.PUBLISHED, contentAudienceBadgeDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.FINALIZING, contentAudienceBadgeDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.FINALIZED, contentAudienceBadgeDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.REVOKED, contentAudienceBadgeDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.DISTRIBUTED, contentAudienceBadgeDisplayNames.get(ContentAudience.PUBLIC)],
]);

// @ts-expect-error TS2769 - strict migration
export const workflowStateToVisibilityMap = new Map<WorkflowState, string>([
  [WorkflowState.PUBLISHER_REVIEW, contentAudienceDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.PUBLISHED, contentAudienceDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.FINALIZING, contentAudienceDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.FINALIZED, contentAudienceDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.REVOKED, contentAudienceDisplayNames.get(ContentAudience.INTERNAL)],
  [WorkflowState.DISTRIBUTED, contentAudienceDisplayNames.get(ContentAudience.PUBLIC)],
]);

export const workflowStateDisplayNames = new Map<WorkflowState, string>([
  [WorkflowState.NEW, 'New'],
  [WorkflowState.UNMODIFIED, 'Unmodified'],
  [WorkflowState.MODIFIED, 'Modified'],
  [WorkflowState.PENDING_REVIEW, 'Pending review'],
  [WorkflowState.IN_REVIEW, 'In Review'],
  [WorkflowState.REJECTED, 'Rejected'],
  [WorkflowState.PUBLISHED, 'AWS'],
  [WorkflowState.UNPUBLISHED, 'Unpublished'],
  [WorkflowState.REVIEWED, 'Reviewed'],
  [WorkflowState.FINALIZED, 'AWS'],
  [WorkflowState.DISTRIBUTED, 'Public'],
  [WorkflowState.FINALIZING, 'Finalizing'],
  [WorkflowState.REVOKED, 'Revoked (Public)'],
  [WorkflowState.PREAUTHORING_REVIEW, 'Pre-authoring Review'],
  [WorkflowState.PREAUTHORING_REJECTED, 'Pre-authoring Rejected'],
  [WorkflowState.PREAUTHORING_MORE_INFO_NEEDED, 'Pre-authoring Need More Information'],
  [WorkflowState.PROPOSAL, 'Proposal'],
  [WorkflowState.QUALITY_CHECK, 'Quality check'],
  [WorkflowState.PUBLISHER_REVIEW, 'Publisher review'],
]);

export const publicationStateDisplayNames = new Map<string, string>([
  ['AWS', 'AWS'],
  ['Public', 'Public'],
]);

export const graphFriendlyMetricTypeDisplayNames = new Map<MetricType, string>([
  [MetricType.Draft, 'Draft'],
  [MetricType.PeerReview, 'Review'],
  [MetricType.PublisherReview, 'Publishing'],
  [MetricType.InternalPublished, 'AWS'],
  [MetricType.TechReview, 'Finalizing'],
  [MetricType.ExternalPublished, 'APN'],
  [MetricType.Public, 'APG'],
]);

export const finalizingContentDisplayNames = new Map<FinalizingGraphState, string>([
  [FinalizingGraphState.Unassigned, 'Unassigned'],
  [FinalizingGraphState.Assigned, 'Assigned'],
  [FinalizingGraphState.Finalizing, 'Finalizing'],
  [FinalizingGraphState.Finalized, 'Published to APG'],
  [FinalizingGraphState.Distributed, 'Published to Public'],
]);

export const rTypeDisplayNames = new Map<RType, string>([
  [RType.ReArchitect, 'Re-architect'],
  [RType.ReHost, 'Rehost'],
  [RType.ReLocate, 'Relocate'],
  [RType.RePlatform, 'Replatform'],
  [RType.RePurchase, 'Repurchase'],
  [RType.Retire, 'Retire'],
  [RType.NotApplicable, 'N/A'],
]);

export const patternTypeDisplayNames = new Map<PatternType, string>([
  [PatternType.PatternCloudNative, 'Cloud Native'],
  [PatternType.PatternMigration, 'Migration'],
  [PatternType.PatternProcess, 'Process'],
  [PatternType.PatternOptimization, 'Optimization'],
  [PatternType.PatternGovernance, 'Governance'],
  [PatternType.PatternSecurityAndCompliance, 'Security and Compliance'],
]);

export const contentSubTypeDisplayNames = new Map<ContentSubType, string>([
  [ContentSubType.GuideMigration, 'Migration Guide'],
  [ContentSubType.StrategyMigration, 'Migration Strategy'],
  [ContentSubType.PatternCloudNative, 'Cloud Native'],
  [ContentSubType.PatternMigration, 'Migration'],
  [ContentSubType.PatternProcess, 'Process'],
  [ContentSubType.PatternOptimization, 'Optimization'],
  [ContentSubType.PatternGovernance, 'Governance'],
  [ContentSubType.PatternSecurityAndCompliance, 'Security and Compliance'],
  [ContentSubType.ReleaseNotes, 'Release notes'],
  [ContentSubType.ComingSoon, 'Coming soon'],
  [ContentSubType.GettingStarted, 'Getting started'],
  [ContentSubType.ArchitectureDiagram, 'Architecture Diagram'],
  [ContentSubType.News, 'News'],
]);

export const lifeCycleDisplayNames = new Map<LifeCycle, string>([
  [LifeCycle.Strategy, 'Strategy'],
  [LifeCycle.Design, 'Design'],
  [LifeCycle.Transition, 'Transition'],
  [LifeCycle.Operation, 'Operation'],
  [LifeCycle.ContinualImprovement, 'Continual Improvement'],
]);

export const patternAccreditationDisplayNames = new Map<PatternAccreditation, string>([
  [PatternAccreditation.AWSAccredited, 'AWS Accredited'],
  [PatternAccreditation.ISVAccredited, 'ISV Accredited'],
  [PatternAccreditation.CustomerAccredited, 'Customer Accredited'],
  [PatternAccreditation.Community, 'Community'],
  [PatternAccreditation.WellArchitectedFramework, 'Well Architected Framework'],
]);

export const patternProvenanceDisplayNames = new Map<PatternProvenance, string>([
  [PatternProvenance.Tested, 'Tested (lab, Poc, Pilot)'],
  [PatternProvenance.ProductionDeployed, 'Production Deployed'],
  [PatternProvenance.ArchitecturalGuide, 'Architectural Guide (untested)'],
]);

export const environmentDisplayNames = new Map<Environment, string>([
  [Environment.PocPilot, 'PoC or pilot'],
  [Environment.Production, 'Production'],
]);

export const isvDisplayNames = new Map<IndependentSoftwareVendor, string>([
  [IndependentSoftwareVendor.Citrix, 'Citrix'],
  [IndependentSoftwareVendor.IBM, 'IBM'],
  [IndependentSoftwareVendor.Microsoft, 'Microsoft'],
  [IndependentSoftwareVendor.Oracle, 'Oracle'],
  [IndependentSoftwareVendor.Splunk, 'Splunk'],
  [IndependentSoftwareVendor.VMWare, 'VMWare'],
  [IndependentSoftwareVendor.FortySevenLining, '47Lining'],
  [IndependentSoftwareVendor.Alfresco, 'Alfresco'],
  [IndependentSoftwareVendor.Apache, 'Apache'],
  [IndependentSoftwareVendor.Atlassian, 'Atlassian'],
  [IndependentSoftwareVendor.Aviatrix, 'Aviatrix'],
  [IndependentSoftwareVendor.AWX, 'AWX'],
  [IndependentSoftwareVendor.Bitnami, 'Bitnami'],
  [IndependentSoftwareVendor.CheckPoint, 'Check Point'],
  [IndependentSoftwareVendor.Chef, 'Chef'],
  [IndependentSoftwareVendor.Cisco, 'Cisco'],
  [IndependentSoftwareVendor.Cloudera, 'Cloudera'],
  [IndependentSoftwareVendor.CloudStax, 'CloudStax'],
  [IndependentSoftwareVendor.Confluent, 'Confluent'],
  [IndependentSoftwareVendor.Corda, 'Corda'],
  [IndependentSoftwareVendor.Couchbase, 'Couchbase'],
  [IndependentSoftwareVendor.DataStax, 'DataStax'],
  [IndependentSoftwareVendor.Docker, 'Docker'],
  [IndependentSoftwareVendor.Drupal, 'Drupal'],
  [IndependentSoftwareVendor.Dynatrace, 'Dynatrace'],
  [IndependentSoftwareVendor.F5, 'F5'],
  [IndependentSoftwareVendor.Fortinet, 'Fortinet'],
  [IndependentSoftwareVendor.GitHub, 'GitHub'],
  [IndependentSoftwareVendor.Hashicorp, 'Hashicorp'],
  [IndependentSoftwareVendor.InfoR, 'InfoR'],
  [IndependentSoftwareVendor.Informatica, 'Informatica'],
  [IndependentSoftwareVendor.Magento, 'Magento'],
  [IndependentSoftwareVendor.McAfee, 'McAfee'],
  [IndependentSoftwareVendor.MongoDB, 'MongoDB'],
  [IndependentSoftwareVendor.NetApp, 'NetApp'],
  [IndependentSoftwareVendor.NGINX, 'NGINX'],
  [IndependentSoftwareVendor.OpenSource, 'Open Source'],
  [IndependentSoftwareVendor.PaloAlto, 'Palo Alto'],
  [IndependentSoftwareVendor.Pivotal, 'Pivotal'],
  [IndependentSoftwareVendor.Puppet, 'Puppet'],
  [IndependentSoftwareVendor.Qubole, 'Qubole'],
  [IndependentSoftwareVendor.Redhat, 'Redhat'],
  [IndependentSoftwareVendor.SAP, 'SAP'],
  [IndependentSoftwareVendor.SAS, 'SAS'],
  [IndependentSoftwareVendor.SIOS, 'SIOS'],
  [IndependentSoftwareVendor.Solace, 'Solace'],
  [IndependentSoftwareVendor.Sophos, 'Sophos'],
  [IndependentSoftwareVendor.SpotInst, 'SpotInst'],
  [IndependentSoftwareVendor.Swift, 'Swift'],
  [IndependentSoftwareVendor.Symantec, 'Symantec'],
  [IndependentSoftwareVendor.Tableau, 'Tableau'],
  [IndependentSoftwareVendor.Talend, 'Talend'],
  [IndependentSoftwareVendor.Teradici, 'Teradici'],
  [IndependentSoftwareVendor.TIBCO, 'TIBCO'],
  [IndependentSoftwareVendor.TrendMicro, 'TrendMicro'],
  [IndependentSoftwareVendor.WANdisco, 'WANdisco'],
]);

export const taskSkillDisplayNames = new Map<TaskSkill, string>([
  [TaskSkill.AppOwner, 'App owner'],
  [TaskSkill.AWSAdministrator, 'AWS administrator'],
  [TaskSkill.CloudAdministrator, 'Cloud administrator'],
  [TaskSkill.DBA, 'DBA'],
  [TaskSkill.NetworkAdministrator, 'Network administrator'],
  [TaskSkill.AWSSystemsAdministrator, 'AWS systems administrator'],
  [TaskSkill.AppDeveloper, 'App developer'],
  [TaskSkill.DataScientist, 'Data scientist'],
  [TaskSkill.CloudArchitect, 'Cloud architect'],
  [TaskSkill.DataEngineer, 'Data engineer'],
  [TaskSkill.DevOpsEngineer, 'DevOps engineer'],
  [TaskSkill.MLEngineer, 'ML engineer'],
  [TaskSkill.MigrationEngineer, 'Migration engineer'],
  [TaskSkill.TestEngineer, 'Test engineer'],
  [TaskSkill.AWSDevOps, 'AWS DevOps'],
  [TaskSkill.BuildLead, 'Build lead'],
  [TaskSkill.CutoverLead, 'Cutover lead'],
  [TaskSkill.MigrationLead, 'Migration lead'],
  [TaskSkill.GeneralAWS, 'General AWS'],
  [TaskSkill.Other, 'Other (type in custom skill)'],
]);

export const technologyDisplayNames = new Map<Technology, string>([
  [Technology.BlockchainConsortium, 'Blockchain Consortium'],
  [Technology.BlockchainPublic, 'Blockchain Public'],
  [Technology.BlockchainPrivate, 'Blockchain Private'],
  [Technology.BusinessIntelligence, 'Business Intelligence'],
  [Technology.Collaboration, 'Collaboration'],
  [Technology.ContentManagement, 'Content Management'],
  [Technology.CRM, 'CRM'],
  [Technology.eCommerce, 'eCommerce'],
  [Technology.EducationAndResearch, 'Education & Research'],
  [Technology.ERP, 'ERP'],
  [Technology.FinancialServices, 'Financial Services'],
  [Technology.HealthcareAndLifeSciences, 'Healthcare & Life Sciences'],
  [Technology.Media, 'Media'],
  [Technology.ProjectManagement, 'Project Management'],
  [Technology.SAP, 'SAP'],
  [Technology.ContentDelivery, 'Content Delivery'],
  [Technology.DataLake, 'Data Lake'],
  [Technology.DataWarehouse, 'Data Warehouse'],
  [Technology.DataManagement, 'Data Management'],
  [Technology.APAndBilling, 'AP and Billing'],
  [Technology.ApplicationAndWebDevelopment, 'Application and Web Development'],
  [Technology.CADAndCAM, 'CAD and CAM'],
  [Technology.GISAndMapping, 'GIS and Mapping'],
  [Technology.IllustrationAndDesign, 'Illustration and Design'],
  [Technology.MediaAndEncoding, 'Media and Encoding'],
  [Technology.ProductivityAndCollaboration, 'Productivity and Collaboration'],
  [Technology.SecurityStorageArchiving, 'Security/Storage/Archiving'],
  [Technology.Utilities, 'Utilities'],
  [Technology.IssueAndBugTracking, 'Issue & Bug Tracking'],
  [Technology.Monitoring, 'Monitoring'],
  [Technology.LogAnalysis, 'Log Analysis'],
  [Technology.SourceControl, 'Source Control'],
  [Technology.Testing, 'Testing'],
  [Technology.AMS, 'AMS'],
  [Technology.EnterpriseWorkloads, 'Enterprise Workloads'],
  [Technology.Mobility, 'Mobility'],
  [Technology.BackupAndRecovery, 'Backup and Recover'],
  [Technology.LoggingAndMonitoring, 'Logging and Monitoring'],
  [Technology.DevOps, 'DevOps'],
  [Technology.ConfigurationManagement, 'Configuration Management'],
  [Technology.ChangeManagement, 'Change Management'],
  [Technology.Provisioning, 'Provisioning'],
  [Technology.TestDataManagement, 'Test Data Management'],
  [Technology.SoftwareInfrastructure, 'Software Infrastructure'],
  [Technology.Automation, 'Automation'],
  [Technology.ApplicationDevelopment, 'Application Development'],
  [Technology.AppServer, 'App Server'],
  [Technology.ApplicationStacks, 'Application Stacks'],
  [Technology.BigData, 'Big Data'],
  [Technology.CloudNative, 'Cloud Native'],
  [Technology.Compliance, 'Compliance'],
  [Technology.Containers, 'Containers'],
  [Technology.DatabasesAndCaching, 'Databases & Caching'],
  [Technology.Directories, 'Directories'],
  [Technology.Federation, 'Federation'],
  [Technology.HighPerformanceComputing, 'High Performance Computing'],
  [Technology.Hybrid, 'Hybrid'],
  [Technology.ClusteringAndHighAvailability, 'Clustering and High Availability'],
  [Technology.Messaging, 'Messaging'],
  [Technology.MicroServices, 'Micro-Services'],
  [Technology.Migration, 'Migration'],
  [Technology.NetworkInfrastructure, 'Network Infrastructure'],
  [Technology.OperatingSystems, 'Operating Systems'],
  [Technology.Orchestration, 'Orchestration'],
  [Technology.SaaS, 'SaaS'],
  [Technology.SSO, 'SSO'],
  [Technology.Security, 'Security'],
  [Technology.StorageAndBackup, 'Storage & Backup'],
  [Technology.WebServer, 'Web Server'],
  [Technology.IoTAnalytics, 'IoT Analytics'],
  [Technology.IoTApplications, 'IoT Applications'],
  [Technology.IoTDeviceConnectivity, 'IoT Device Connectivity'],
  [Technology.IoTDeviceManagement, 'IoT Device Management'],
  [Technology.IoTDeviceSecurity, 'IoT Device Security'],
  [Technology.IoTSmartHomeAndCity, 'IoT Smart Home & City'],
  [Technology.IndustrialIoT, 'Industrial IoT'],
  [Technology.MLSolutions, 'Machine Learning Solutions'],
  [Technology.MLDataLabelingServices, 'Machine Learning DataLabeling Services'],
  [Technology.MLComputerVision, 'Machine Learning Computer Vision'],
  [Technology.MLNaturalLanguageProcessing, 'Machine Learning Natural Language Processing'],
  [Technology.MLSpeechRecognition, 'Machine Learning Speech Recognition'],
  [Technology.MLText, 'Machine Learning Text'],
  [Technology.MLImage, 'Machine Learning Image'],
  [Technology.MLVideo, 'Machine Learning Video'],
  [Technology.MLAudio, 'Machine Learning Audio'],
  [Technology.MLStructured, 'Machine Learning Structured'],
  [Technology.PlatformMigrations, 'Platform Migrations'],
  [Technology.Mainframe, 'Mainframe'],
  [Technology.Solutions, 'Solutions'],
  [Technology.AMSManagedServices, 'AMS Managed Services'],
]);

export const workloadDisplayNames = new Map<Workload, string>([
  [Workload.IBM, 'IBM'],
  [Workload.Microsoft, 'Microsoft'],
  [Workload.Oracle, 'Oracle'],
  [Workload.SAP, 'SAP'],
  [Workload.OpenSource, 'Open-source'],
  [Workload.Other, 'All other workloads'],
]);

// temporarily include list until MCM to remove deprecated, duplicated, rebranded services from contents
export const deprecatedAWSServices: AWSService[] = [
  AWSService.ADConnector,
  AWSService.AlexaForBusiness,
  AWSService.AmazonVerifiedPermission, // name update
  AWSService.AppDiscoveryService,
  AWSService.BugBust,
  AWSService.Chatbot,
  AWSService.CloudEndureDisasterRecovery,
  AWSService.CloudWatchEvidently,
  AWSService.CodeStar,
  AWSService.CodeWhisperer,
  AWSService.DeepComposer,
  AWSService.DeepLens,
  AWSService.DirectoryServiceForMicrosoftAd,
  AWSService.EC2ContainerRegistry,
  AWSService.ElasticInference,
  AWSService.ElasticSearch, // duplicate
  AWSService.ElasticTranscoder,
  AWSService.FaultInjectionSimulator, // rebranded
  AWSService.GameSparks,
  AWSService.Grafana, // duplicate
  AWSService.IoT1Click,
  AWSService.IoTAnalytics,
  AWSService.IoTThingsGraph,
  AWSService.KinesisDataFirehose, // rebranded
  AWSService.KinesisDataAnalytics,
  AWSService.LookoutForMetrics,
  AWSService.LookoutForVision,
  AWSService.Lumberyard,
  AWSService.MachineLearning,
  AWSService.ManagedServices, // duplicate
  AWSService.MediaStore,
  AWSService.NimbleStudio,
  AWSService.OpsWorks,
  AWSService.PersonalHealthDashboard, // rebranded
  AWSService.PrivateSG, // name update
  AWSService.Private5G,
  AWSService.QLDB,
  AWSService.RoboMaker,
  AWSService.SdkForJavaScriptInNodeJs,
  AWSService.SdkForNetAndXamarin,
  AWSService.ServerMigrationService,
  AWSService.ServiceHealthDashboard,
  AWSService.SimpleAD,
  AWSService.Snowcone,
  AWSService.Snowmobile,
  AWSService.SSO, // duplicate
  AWSService.Sumerian,
  AWSService.VMwareCloudOnAWS,
  AWSService.WAM,
  AWSService.WellArchitected,
  AWSService.WorkLink,
  AWSService.WorkSpacesWeb, // rebranded
  AWSService.WorkSpacesApplicationManager,
];

export const awsServiceDisplayNames = new Map<AWSService, string>([
  [AWSService.Alexa, 'Alexa'],
  [AWSService.AmazonVerifiedPermissions, 'Amazon Verified Permissions'],
  [AWSService.Amplify, 'Amplify'],
  [AWSService.AMS, 'AMS'],
  [AWSService.ApiGateway, 'API Gateway'],
  [AWSService.AppConfig, 'AppConfig'],
  [AWSService.ApplicationAutoScaling, 'Application Auto Scaling'],
  [AWSService.AppComposer, 'Application Composer'],
  [AWSService.ApplicationCostProfiler, 'Application Cost Profiler'],
  [AWSService.AppDiscoveryService, 'Application Discovery Service'],
  [AWSService.AppFabric, 'AppFabric'],
  [AWSService.AppFlow, 'AppFlow'],
  [AWSService.AppStudio, 'App Studio'],
  [AWSService.Appmesh, 'App Mesh'],
  [AWSService.AppRunner, 'App Runner'],
  [AWSService.App2Container, 'App2Container'],
  [AWSService.AppMigrationService, 'MGN'],
  [AWSService.Appstream, 'AppStream 2.0'],
  [AWSService.Appsync, 'AppSync'],
  [AWSService.Artifact, 'Artifact'],
  [AWSService.Athena, 'Athena'],
  [AWSService.AuditManager, 'Audit Manager'],
  [AWSService.AugmentedAi, 'A2I'],
  [AWSService.Aurora, 'Aurora'],
  [AWSService.AutoScaling, 'Auto Scaling'],
  [AWSService.B2BDI, 'B2B Data Interchange'],
  [AWSService.BackintAgent, 'Backint Agent'],
  [AWSService.Backup, 'Backup'],
  [AWSService.Batch, 'Batch'],
  [AWSService.Bedrock, 'Bedrock'],
  [AWSService.BedrockAgentCore, 'AgentCore'],
  [AWSService.BillingAndCostManagement, 'Billing and Cost Management'],
  [AWSService.BlockchainTemplates, 'Blockchain Templates'],
  [AWSService.Blox, 'Blox'],
  [AWSService.Bottlerocket, 'Bottlerocket'],
  [AWSService.Braket, 'Braket'],
  [AWSService.BugBust, 'BugBust'],
  [AWSService.CertificateManager, 'ACM'],
  [AWSService.Chalice, 'Chalice'],
  [AWSService.Chatbot, 'Chatbot'],
  [AWSService.CDISdk, 'CDI SDK'],
  [AWSService.CDK, 'CDK'],
  [AWSService.Chime, 'Chime'],
  [AWSService.ChimeSDK, 'Chime SDK'],
  [AWSService.CleanRooms, 'Clean Rooms'],
  [AWSService.ClientVpn, 'Client VPN'],
  [AWSService.CloudControl, 'Cloud Control API'],
  [AWSService.CloudDirectory, 'Cloud Directory'],
  [AWSService.CloudMap, 'Cloud Map'],
  [AWSService.CloudWAN, 'Cloud WAN'],
  [AWSService.Cloud9, 'Cloud9'],
  [AWSService.CloudFormation, 'CloudFormation'],
  [AWSService.CloudFront, 'CloudFront'],
  [AWSService.CloudHSM, 'CloudHSM'],
  [AWSService.CloudSearch, 'CloudSearch'],
  [AWSService.CloudShell, 'CloudShell'],
  [AWSService.CloudTrail, 'CloudTrail'],
  [AWSService.CloudWatch, 'CloudWatch'],
  [AWSService.CloudWatchApplicationInsights, 'CloudWatch Application Insights'],
  [AWSService.CloudWatchEvents, 'CloudWatch Events'],
  [AWSService.CloudWatchEvidently, 'CloudWatch Evidently'],
  [AWSService.CloudWatchInternetMonitor, 'CloudWatch Internet Monitor'],
  [AWSService.CloudWatchLogs, 'CloudWatch Logs'],
  [AWSService.CloudWatchRUM, 'CloudWatch RUM'],
  [AWSService.CodeArtifact, 'CodeArtifact'],
  [AWSService.CodeBuild, 'CodeBuild'],
  [AWSService.CodeCatalyst, 'CodeCatalyst'],
  [AWSService.CodeCommit, 'CodeCommit'],
  [AWSService.CodeDeploy, 'CodeDeploy'],
  [AWSService.CodeGuru, 'CodeGuru'],
  [AWSService.CodePipeline, 'CodePipeline'],
  [AWSService.CodeStar, 'CodeStar'],
  [AWSService.CommandLineInterface, 'CLI'],
  [AWSService.Cognito, 'Cognito'],
  [AWSService.Comprehend, 'Comprehend'],
  [AWSService.ComprehendMedical, 'Comprehend Medical'],
  [AWSService.ComputeOptimizer, 'Compute Optimizer'],
  [AWSService.Config, 'Config'],
  [AWSService.Connect, 'Connect'],
  [AWSService.ConsoleMobileApplication, 'Console Mobile Application'],
  [AWSService.ControlTower, 'Control Tower'],
  [AWSService.Corretto, 'Corretto'],
  [AWSService.DataExchange, 'Data Exchange'],
  [AWSService.DataLifecycleManager, 'Data Lifecycle Manager'],
  [AWSService.DataPipeline, 'Data Pipeline'],
  [AWSService.DatabaseMigrationService, 'DMS'],
  [AWSService.DataSync, 'DataSync'],
  [AWSService.DataZone, 'DataZone'],
  [AWSService.DCV, 'DCV'],
  [AWSService.DeadlineCloud, 'Deadline Cloud'],
  [AWSService.DeepLearningAmIs, 'Deep Learning AMIs'],
  [AWSService.DeepLearningContainers, 'Deep Learning Containers'],
  [AWSService.DeepComposer, 'DeepComposer'],
  [AWSService.DeepLens, 'DeepLens'],
  [AWSService.DeepRacer, 'DeepRacer'],
  [AWSService.Detective, 'Detective'],
  [AWSService.DeviceFarm, 'Device Farm'],
  [AWSService.DevOpsAgent, 'DevOps Agent'],
  [AWSService.DevOpsGuru, 'DevOps Guru'],
  [AWSService.DevPay, 'DevPay'],
  [AWSService.DirectConnect, 'Direct Connect'],
  [AWSService.DirectoryService, 'Directory Service'],
  [AWSService.DocumentDB, 'DocumentDB'],
  [AWSService.DynamoDB, 'DynamoDB'],
  [AWSService.DynamoDbStreams, 'DynamoDB Streams'],
  [AWSService.EC2AutoScaling, 'EC2 Auto Scaling'],
  [AWSService.ElasticBeanstalk, 'Elastic Beanstalk'],
  [AWSService.EBS, 'EBS'],
  [AWSService.EC2, 'EC2'],
  [AWSService.ECR, 'ECR'],
  [AWSService.ElasticContainerService, 'ECS'],
  [AWSService.EFS, 'EFS'],
  [AWSService.ElasticContainerServiceKubernetes, 'EKS'],
  [AWSService.ElasticDisasterRecovery, 'Elastic Disaster Recovery'],
  [AWSService.ElasticVMwareService, 'EVS'],
  [AWSService.ElasticInference, 'Elastic Inference'],
  [AWSService.ElasticTranscoder, 'Elastic Transcoder'],
  [AWSService.ElastiCache, 'ElastiCache'],
  [AWSService.Elemental, 'Elemental'],
  [AWSService.ELB, 'ELB'],
  [AWSService.EMR, 'EMR'],
  [AWSService.EndUserMessagingSMS, 'End User Messaging SMS'],
  [AWSService.EndUserMessagingSocial, 'End User Messaging Social'],
  [AWSService.EntityResolution, 'Entity Resolution'],
  [AWSService.EventBridge, 'EventBridge'],
  [AWSService.Fargate, 'Fargate'],
  [AWSService.FaultInjection, 'Fault Injection Service'],
  [AWSService.FinSpace, 'FinSpace'],
  [AWSService.FirewallManager, 'Firewall Manager'],
  [AWSService.ForeCast, 'Forecast'],
  [AWSService.FraudDetector, 'Fraud Detector'],
  [AWSService.FreeRTOS, 'FreeRTOS'],
  [AWSService.FSx, 'FSx'],
  [AWSService.FSxForLustre, 'FSx for Lustre'],
  [AWSService.FSxForONTAP, 'FSx for ONTAP'],
  [AWSService.FSxForOpenZFS, 'FSx for OpenZFS'],
  [AWSService.FSxForWindowsFileServer, 'FSx for Windows File Server'],
  [AWSService.GameKit, 'GameKit'],
  [AWSService.GameLift, 'GameLift'],
  [AWSService.Glacier, 'Glacier'],
  [AWSService.GlobalAccelerator, 'Global Accelerator'],
  [AWSService.Glue, 'Glue'],
  [AWSService.GovCloud, 'GovCloud'],
  [AWSService.Greengrass, 'Greengrass'],
  [AWSService.GroundStation, 'Ground Station'],
  [AWSService.GroundTruth, 'Ground Truth'],
  [AWSService.GuardDuty, 'GuardDuty'],
  [AWSService.Health, 'Health'],
  [AWSService.HealthImaging, 'HealthImaging'],
  [AWSService.HealthLake, 'HealthLake'],
  [AWSService.HealthScribe, 'HealthScribe'],
  [AWSService.Honeycode, 'Honeycode'],
  [AWSService.IAM, 'IAM'],
  [AWSService.IAMIdentityCenter, 'IAM Identity Center'],
  [AWSService.IAMAccessAnalyzer, 'IAM Access Analyzer'],
  [AWSService.ImageBuilder, 'EC2 Image Builder'],
  [AWSService.ImportExport, 'Import/Export'],
  [AWSService.Inspector, 'Inspector'],
  [AWSService.IoT, 'IoT Core'],
  [AWSService.IoTAnalytics, 'IoT Analytics'],
  [AWSService.IoTCoreForLoRaWan, 'IoT Core for LoRaWAN'],
  [AWSService.IoTDeviceDefender, 'IoT Device Defender'],
  [AWSService.IoTDeviceManagement, 'IoT Device Management'],
  [AWSService.IoTEvents, 'IoT Events'],
  [AWSService.IoTFleetWise, 'IoT FleetWise'],
  [AWSService.IoTGreengrass, 'IoT Greengrass'],
  [AWSService.IoTRoboRunner, 'IoT RoboRunner'],
  [AWSService.IoTSiteWise, 'IoT SiteWise'],
  [AWSService.IoTTwinMaker, 'IoT TwinMaker'],
  [AWSService.IoTWireless, 'IoT Wireless'],
  [AWSService.IoT1Click, 'IoT 1-Click'],
  [AWSService.IQ, 'IQ'],
  [AWSService.IVS, 'IVS'],
  [AWSService.KeyManagementService, 'KMS'],
  [AWSService.Kendra, 'Kendra'],
  [AWSService.Keyspaces, 'Keyspaces'],
  [AWSService.Kinesis, 'Kinesis'],
  [AWSService.DataFirehose, 'Data Firehose'],
  [AWSService.KinesisDataStreams, 'Kinesis Data Streams'],
  [AWSService.KinesisVideoStreams, 'Kinesis Video Streams'],
  [AWSService.Kiro, 'Kiro'],
  [AWSService.LakeFormation, 'Lake Formation'],
  [AWSService.Lambda, 'Lambda'],
  [AWSService.LaunchWizard, 'Launch Wizard'],
  [AWSService.Lex, 'Lex'],
  [AWSService.LicenseManager, 'License Manager'],
  [AWSService.LightSail, 'Lightsail'],
  [AWSService.LocalZones, 'Local Zones'],
  [AWSService.LocationService, 'Location Service'],
  [AWSService.LookoutForEquipment, 'Lookout for Equipment'],
  [AWSService.LookoutForMetrics, 'Lookout for Metrics'],
  [AWSService.LookoutForVision, 'Lookout for Vision'],
  [AWSService.Lumberyard, 'Lumberyard'],
  [AWSService.Macie, 'Macie'],
  [AWSService.MainframeModernization, 'Mainframe Modernization'],
  [AWSService.ManagedBlockchain, 'Managed Blockchain'],
  [AWSService.ManagedServiceForGrafana, 'Managed Grafana'],
  [AWSService.ManagedServiceForFlink, 'Managed Service for Flink'],
  [AWSService.ManagementConsole, 'Management Console'],
  [AWSService.ManagementPortalForVCenter, 'Management Portal for vCenter'],
  [AWSService.Marketplace, 'Marketplace'],
  [AWSService.MechanicalTurk, 'Mechanical Turk'],
  [AWSService.MediaConnect, 'MediaConnect'],
  [AWSService.MediaConvert, 'MediaConvert'],
  [AWSService.MediaLive, 'MediaLive'],
  [AWSService.MediaPackage, 'MediaPackage'],
  [AWSService.MediaStore, 'MediaStore'],
  [AWSService.MediaTailor, 'MediaTailor'],
  [AWSService.ElementalOnPremises, 'Elemental appliances and software'],
  [AWSService.MemoryDB, 'MemoryDB'],
  [AWSService.MicroserviceExtractorForNet, 'Microservice Extractor for .NET'],
  [AWSService.MigrationHub, 'Migration Hub'],
  [AWSService.MigrationEvaluator, 'Migration Evaluator'],
  [AWSService.MobileAnalytics, 'Mobile Analytics'],
  [AWSService.MobileHub, 'Mobile Hub'],
  [AWSService.Monitron, 'Monitron'],
  [AWSService.MQ, 'MQ'],
  [AWSService.MSK, 'MSK'],
  [AWSService.MWAA, 'MWAA'],
  [AWSService.MxNet, 'MXNet'],
  [AWSService.Neptune, 'Neptune'],
  [AWSService.NetworkFirewall, 'Network Firewall'],
  [AWSService.NimbleStudio, 'Nimble Studio'],
  [AWSService.Nova, 'Nova'],
  [AWSService.NovaAct, 'Nova Act'],
  [AWSService.Omics, 'HealthOmics'],
  [AWSService.OpenSearchService, 'OpenSearch Service'],
  [AWSService.OpsWorks, 'OpsWorks'],
  [AWSService.OpsWorksForChefAutomate, 'OpsWorks for Chef Automate'],
  [AWSService.OpsWorksStacks, 'OpsWorks Stacks'],
  [AWSService.OracleDatabaseAWS, 'Oracle Database@AWS'],
  [AWSService.Organizations, 'Organizations'],
  [AWSService.OutPosts, 'Outposts'],
  [AWSService.Panorama, 'Panorama'],
  [AWSService.PaymentCryptography, 'Payment Cryptography'],
  [AWSService.ParallelCluster, 'ParallelCluster'],
  [AWSService.ParallelComputingService, 'PCS'],
  [AWSService.HealthDashboard, 'Health Dashboard'],
  [AWSService.Personalize, 'Personalize'],
  [AWSService.Pinpoint, 'Pinpoint'],
  [AWSService.Private5G, 'Private 5G'],
  [AWSService.Polly, 'Polly'],
  [AWSService.PrivateCa, 'Private CA'],
  [AWSService.Prometheus, 'Managed Service for Prometheus'],
  [AWSService.Proton, 'Proton'],
  [AWSService.Q, 'Q'],
  [AWSService.QBusiness, 'Q Business'],
  [AWSService.QDeveloper, 'Q Developer'],
  [AWSService.QLDB, 'QLDB'],
  [AWSService.QuickSight, 'Quick Sight'],
  [AWSService.QuickSuite, 'Quick Suite'],
  [AWSService.RDS, 'RDS'],
  [AWSService.ReachabilityAnalyzer, 'Reachability Analyzer'],
  [AWSService.RedShift, 'Redshift'],
  [AWSService.Rekognition, 'Rekognition'],
  [AWSService.RepostPrivate, 're:Post Private'],
  [AWSService.ResilienceHub, 'Resilience Hub'],
  [AWSService.ResourceGroups, 'Resource Groups'],
  [AWSService.ResourceAccessManager, 'Resource Access Manager'],
  [AWSService.ResourceExplorer, 'Resource Explorer'],
  [AWSService.RoboMaker, 'RoboMaker'],
  [AWSService.ROSA, 'ROSA'],
  [AWSService.Route53, 'Route 53'],
  [AWSService.RTBFabric, 'RTB Fabric'],
  [AWSService.SageMaker, 'SageMaker'],
  [AWSService.SavingsPlans, 'Savings Plans'],
  [AWSService.S3, 'S3'],
  [AWSService.SAM, 'SAM'],
  [AWSService.SCT, 'SCT'],
  [AWSService.SdkForAndroid, 'Mobile SDK for Android'],
  [AWSService.SdkForC, 'SDK for C++'],
  [AWSService.SdkForGo, 'SDK for Go'],
  [AWSService.SdkForIOs, 'Mobile SDK for iOS'],
  [AWSService.SdkForJava, 'SDK for Java'],
  [AWSService.SdkForJavaScriptInTheBrowser, 'SDK for JavaScript'],
  [AWSService.SdkForNet, 'SDK for .NET'],
  [AWSService.SdkForPhp, 'SDK for PHP'],
  [AWSService.SdkForPythonBoto3, 'SDK for Python (Boto3)'],
  [AWSService.SdkForRuby, 'Mobile SDK for Ruby'],
  [AWSService.SdkForSapAbap, 'SDK for SAP ABAP'],
  [AWSService.SdkForUnity, 'Mobile SDK for Unity'],
  [AWSService.SecretsManager, 'Secrets Manager'],
  [AWSService.SecurityAgent, 'Security Agent'],
  [AWSService.SecurityHub, 'Security Hub'],
  [AWSService.SecurityHubCSPM, 'Security Hub CSPM'],
  [AWSService.SecurityIncidentResponse, 'Security Incident Response'],
  [AWSService.SecurityLake, 'Security Lake'],
  [AWSService.ServelessApplicationRepository, 'Serverless Application Repository'],
  [AWSService.ServerMigrationService, 'Server Migration Service'],
  [AWSService.ServiceCatalog, 'Service Catalog'],
  [AWSService.ServiceQuotas, 'Service Quotas'],
  [AWSService.SES, 'SES'],
  [AWSService.Shield, 'Shield'],
  [AWSService.Signer, 'Signer'],
  [AWSService.Silk, 'Silk'],
  [AWSService.SimpleDb, 'SimpleDB'],
  [AWSService.SimSpaceWeaver, 'SimSpace Weaver'],
  [AWSService.SiteToSiteVpn, 'Site-to-Site VPN'],
  [AWSService.SnowDeviceManagement, 'Snow Device Management'],
  [AWSService.SnowFamily, 'Snow Family'],
  [AWSService.Snowball, 'Snowball'],
  [AWSService.SnowballEdge, 'Snowball Edge'],
  [AWSService.Snowcone, 'Snowcone'],
  [AWSService.Snowmobile, 'Snowmobile'],
  [AWSService.SNS, 'SNS'],
  [AWSService.SQS, 'SQS'],
  [AWSService.StepFunctions, 'Step Functions'],
  [AWSService.StorageGateway, 'Storage Gateway'],
  [AWSService.STS, 'STS'],
  [AWSService.SupplyChain, 'Supply Chain'],
  [AWSService.Support, 'Support'],
  [AWSService.SWF, 'SWF'],
  [AWSService.SystemsManager, 'Systems Manager'],
  [AWSService.TelcoNetworkBuilder, 'Telco Network Builder'],
  [AWSService.Textract, 'Textract'],
  [AWSService.Thinkbox, 'Thinkbox'],
  [AWSService.Timestream, 'Timestream'],
  [AWSService.ToolkitForEclipse, 'Toolkit for Eclipse'],
  [AWSService.ToolkitForJetBrains, 'Toolkit for JetBrains'],
  [AWSService.ToolkitForVisualStudio, 'Toolkit for Visual Studio'],
  [AWSService.ToolkitForVsCode, 'Toolkit for VS Code'],
  [AWSService.ToolkitForAzureDevOps, 'Toolkit for Azure DevOps'],
  [AWSService.ToolsForPowerShell, 'Tools for PowerShell'],
  [AWSService.Transcribe, 'Transcribe'],
  [AWSService.TransferFamily, 'Transfer Family'],
  [AWSService.Transform, 'Transform'],
  [AWSService.Transitgateway, 'Transit Gateway'],
  [AWSService.Translate, 'Translate'],
  [AWSService.TrustedAdvisor, 'Trusted Advisor'],
  [AWSService.UserNotifications, 'User Notifications'],
  [AWSService.VerifiedAccess, 'Verified Access'],
  [AWSService.VPC, 'VPC'],
  [AWSService.VPCLattice, 'VPC Lattice'],
  [AWSService.WAF, 'WAF'],
  [AWSService.Wavelength, 'Wavelength'],
  [AWSService.WellArchitectedTool, 'Well-Architected Tool'],
  [AWSService.Wickr, 'Wickr'],
  [AWSService.WorkDocs, 'WorkDocs'],
  [AWSService.WorkMail, 'WorkMail'],
  [AWSService.WorkSpaces, 'WorkSpaces'],
  [AWSService.WorkSpacesSecureBrowser, 'WorkSpaces Secure Browser'],
  [AWSService.WorkSpacesCore, 'WorkSpaces Core'],
  [AWSService.WorkSpacesThinClient, 'WorkSpaces Thin Client'],
  [AWSService.XRay, 'X-Ray'],
]);

/* Temporary updates to service list:
 * Add (deprecated) next to deprecated services
 * Rebranded services have old and new id mapped to new label
 * Duplicate services are marked as duplicate
 */
export const awsServiceReadonlyDisplayNames = new Map<AWSService, string>([
  [AWSService.ADConnector, 'AD Connector (deprecated)'],
  [AWSService.Alexa, 'Alexa'],
  [AWSService.AlexaForBusiness, 'Alexa for Business (deprecated)'],
  [AWSService.AmazonVerifiedPermission, 'Amazon Verified Permissions'], // name update
  [AWSService.AmazonVerifiedPermissions, 'Amazon Verified Permissions'],
  [AWSService.Amplify, 'AWS Amplify'],
  [AWSService.AMS, 'AWS Managed Services (AMS)'],
  [AWSService.ApiGateway, 'Amazon API Gateway'],
  [AWSService.AppConfig, 'AWS AppConfig'],
  [AWSService.ApplicationAutoScaling, 'AWS Application Auto Scaling'],
  [AWSService.AppComposer, 'AWS Application Composer'],
  [AWSService.ApplicationCostProfiler, 'AWS Application Cost Profiler'],
  [AWSService.AppDiscoveryService, 'AWS Application Discovery Service (deprecated)'],
  [AWSService.AppFabric, 'AWS AppFabric'],
  [AWSService.AppFlow, 'Amazon AppFlow'],
  [AWSService.AppStudio, 'AWS App Studio'],
  [AWSService.Appmesh, 'AWS App Mesh'],
  [AWSService.AppRunner, 'AWS App Runner'],
  [AWSService.App2Container, 'AWS App2Container'],
  [AWSService.AppMigrationService, 'AWS MGN'],
  [AWSService.Appstream, 'Amazon AppStream 2.0'],
  [AWSService.Appsync, 'AWS AppSync'],
  [AWSService.Artifact, 'AWS Artifact'],
  [AWSService.Athena, 'Amazon Athena'],
  [AWSService.AuditManager, 'AWS Audit Manager'],
  [AWSService.AugmentedAi, 'Amazon A2I'],
  [AWSService.Aurora, 'Amazon Aurora'],
  [AWSService.AutoScaling, 'AWS Auto Scaling'],
  [AWSService.B2BDI, 'AWS B2B Data Interchange'],
  [AWSService.BackintAgent, 'AWS Backint Agent'],
  [AWSService.Backup, 'AWS Backup'],
  [AWSService.Batch, 'AWS Batch'],
  [AWSService.Bedrock, 'Amazon Bedrock'],
  [AWSService.BedrockAgentCore, 'Amazon Bedrock AgentCore'],
  [AWSService.BillingAndCostManagement, 'AWS Billing and Cost Management'],
  [AWSService.BlockchainTemplates, 'AWS Blockchain Templates'],
  [AWSService.Blox, 'Blox'],
  [AWSService.Bottlerocket, 'Bottlerocket'],
  [AWSService.Braket, 'Amazon Braket'],
  [AWSService.BugBust, 'AWS BugBust (deprecated)'],
  [AWSService.CertificateManager, 'AWS Certificate Manager (ACM)'],
  [AWSService.Chalice, 'AWS Chalice'],
  [AWSService.Chatbot, 'AWS Chatbot (deprecated)'],
  [AWSService.CDISdk, 'AWS CDI SDK'],
  [AWSService.CDK, 'AWS CDK'],
  [AWSService.Chime, 'Amazon Chime'],
  [AWSService.ChimeSDK, 'Amazon Chime SDK'],
  [AWSService.CleanRooms, 'AWS Clean Rooms'],
  [AWSService.ClientVpn, 'AWS Client VPN'],
  [AWSService.CloudControl, 'AWS Cloud Control API'],
  [AWSService.CloudDirectory, 'Amazon Cloud Directory'],
  [AWSService.CloudMap, 'AWS Cloud Map'],
  [AWSService.CloudWAN, 'AWS Cloud WAN'],
  [AWSService.Cloud9, 'AWS Cloud9'],
  [AWSService.CloudEndureDisasterRecovery, 'CloudEndure Disaster Recovery (deprecated)'],
  [AWSService.CloudFormation, 'AWS CloudFormation'],
  [AWSService.CloudFront, 'Amazon CloudFront'],
  [AWSService.CloudHSM, 'AWS CloudHSM'],
  [AWSService.CloudSearch, 'Amazon CloudSearch'],
  [AWSService.CloudShell, 'AWS CloudShell'],
  [AWSService.CloudTrail, 'AWS CloudTrail'],
  [AWSService.CloudWatch, 'Amazon CloudWatch'],
  [AWSService.CloudWatchApplicationInsights, 'Amazon CloudWatch Application Insights'],
  [AWSService.CloudWatchEvents, 'Amazon CloudWatch Events'],
  [AWSService.CloudWatchEvidently, 'Amazon CloudWatch Evidently (deprecated)'],
  [AWSService.CloudWatchInternetMonitor, 'Amazon CloudWatch Internet Monitor'],
  [AWSService.CloudWatchLogs, 'Amazon CloudWatch Logs'],
  [AWSService.CloudWatchRUM, 'Amazon CloudWatch RUM'],
  [AWSService.CodeArtifact, 'AWS CodeArtifact'],
  [AWSService.CodeBuild, 'AWS CodeBuild'],
  [AWSService.CodeCatalyst, 'Amazon CodeCatalyst'],
  [AWSService.CodeCommit, 'AWS CodeCommit'],
  [AWSService.CodeDeploy, 'AWS CodeDeploy'],
  [AWSService.CodeGuru, 'Amazon CodeGuru'],
  [AWSService.CodePipeline, 'AWS CodePipeline'],
  [AWSService.CodeStar, 'AWS CodeStar (deprecated)'],
  [AWSService.CodeWhisperer, 'Amazon CodeWhisperer (deprecated)'],
  [AWSService.CommandLineInterface, 'AWS CLI'],
  [AWSService.Cognito, 'Amazon Cognito'],
  [AWSService.Comprehend, 'Amazon Comprehend'],
  [AWSService.ComprehendMedical, 'Amazon Comprehend Medical'],
  [AWSService.ComputeOptimizer, 'AWS Compute Optimizer'],
  [AWSService.Config, 'AWS Config'],
  [AWSService.Connect, 'Amazon Connect'],
  [AWSService.ConsoleMobileApplication, 'AWS Console Mobile Application'],
  [AWSService.ControlTower, 'AWS Control Tower'],
  [AWSService.Corretto, 'Amazon Corretto'],
  [AWSService.DataExchange, 'AWS Data Exchange'],
  [AWSService.DataLifecycleManager, 'Amazon Data Lifecycle Manager'],
  [AWSService.DataPipeline, 'AWS Data Pipeline'],
  [AWSService.DatabaseMigrationService, 'AWS DMS'],
  [AWSService.DataSync, 'AWS DataSync'],
  [AWSService.DataZone, 'Amazon DataZone'],
  [AWSService.DevOpsAgent, 'AWS DevOps Agent'],
  [AWSService.DCV, 'Amazon DCV'],
  [AWSService.DeadlineCloud, 'AWS Deadline Cloud'],
  [AWSService.DeepLearningAmIs, 'AWS Deep Learning AMIs (DLAMI)'],
  [AWSService.DeepLearningContainers, 'AWS Deep Learning Containers'],
  [AWSService.DeepComposer, 'AWS DeepComposer (deprecated)'],
  [AWSService.DeepLens, 'AWS DeepLens (deprecated)'],
  [AWSService.DeepRacer, 'DeepRacer on AWS'],
  [AWSService.Detective, 'Amazon Detective'],
  [AWSService.DeviceFarm, 'AWS Device Farm'],
  [AWSService.DevOpsGuru, 'Amazon DevOps Guru'],
  [AWSService.DevPay, 'Amazon DevPay'],
  [AWSService.DirectConnect, 'AWS Direct Connect'],
  [AWSService.DirectoryService, 'AWS Directory Service'],
  [AWSService.DirectoryServiceForMicrosoftAd, 'AWS Managed Microsoft AD (deprecated)'],
  [AWSService.DocumentDB, 'Amazon DocumentDB'],
  [AWSService.DynamoDB, 'Amazon DynamoDB'],
  [AWSService.DynamoDbStreams, 'Amazon DynamoDB Streams'],
  [AWSService.EC2AutoScaling, 'Amazon EC2 Auto Scaling'],
  [AWSService.ElasticBeanstalk, 'AWS Elastic Beanstalk'],
  [AWSService.EBS, 'Amazon EBS'],
  [AWSService.EC2, 'Amazon EC2'],
  [AWSService.EC2ContainerRegistry, 'Amazon EC2 Container Registry (deprecated)'],
  [AWSService.ECR, 'Amazon ECR'],
  [AWSService.ElasticContainerService, 'Amazon ECS'],
  [AWSService.EFS, 'Amazon EFS'],
  [AWSService.ElasticContainerServiceKubernetes, 'Amazon EKS'],
  [AWSService.ElasticDisasterRecovery, 'AWS Elastic Disaster Recovery'],
  [AWSService.ElasticInference, 'Amazon Elastic Inference (deprecated)'],
  [AWSService.ElasticTranscoder, 'Amazon Elastic Transcoder (deprecated)'],
  [AWSService.ElasticVMwareService, 'Amazon EVS'],
  [AWSService.ElastiCache, 'Amazon ElastiCache'],
  [AWSService.Elemental, 'AWS Elemental'],
  [AWSService.ELB, 'Elastic Load Balancing (ELB)'],
  [AWSService.EMR, 'Amazon EMR'],
  [AWSService.ElasticSearch, 'Amazon OpenSearch Service'], // duplicate
  [AWSService.EndUserMessagingSMS, 'AWS End User Messaging SMS'],
  [AWSService.EndUserMessagingSocial, 'AWS End User Messaging Social'],
  [AWSService.EntityResolution, 'AWS Entity Resolution'],
  [AWSService.EventBridge, 'Amazon EventBridge'],
  [AWSService.Fargate, 'AWS Fargate'],
  [AWSService.FaultInjectionSimulator, 'AWS Fault Injection Service'], // rebranded
  [AWSService.FaultInjection, 'AWS Fault Injection Service'],
  [AWSService.FinSpace, 'Amazon FinSpace'],
  [AWSService.FirewallManager, 'AWS Firewall Manager'],
  [AWSService.ForeCast, 'Amazon Forecast'],
  [AWSService.FraudDetector, 'Amazon Fraud Detector'],
  [AWSService.FreeRTOS, 'FreeRTOS'],
  [AWSService.FSx, 'Amazon FSx'],
  [AWSService.FSxForLustre, 'Amazon FSx for Lustre'],
  [AWSService.FSxForONTAP, 'Amazon FSx for NetApp ONTAP'],
  [AWSService.FSxForOpenZFS, 'Amazon FSx for OpenZFS'],
  [AWSService.FSxForWindowsFileServer, 'Amazon FSx for Windows File Server'],
  [AWSService.GameKit, 'AWS GameKit'],
  [AWSService.GameLift, 'Amazon GameLift'],
  [AWSService.GameSparks, 'Amazon GameSparks (deprecated)'],
  [AWSService.Glacier, 'Amazon S3 Glacier'],
  [AWSService.GlobalAccelerator, 'AWS Global Accelerator'],
  [AWSService.Glue, 'AWS Glue'],
  [AWSService.GovCloud, 'AWS GovCloud (US)'],
  [AWSService.Grafana, 'Amazon Managed Grafana'], // duplicate
  [AWSService.Greengrass, 'AWS IoT Greengrass'],
  [AWSService.GroundStation, 'AWS Ground Station'],
  [AWSService.GroundTruth, 'Amazon SageMaker Ground Truth'],
  [AWSService.GuardDuty, 'Amazon GuardDuty'],
  [AWSService.Health, 'AWS Health'],
  [AWSService.HealthImaging, 'AWS HealthImaging'],
  [AWSService.HealthLake, 'AWS HealthLake'],
  [AWSService.HealthScribe, 'AWS HealthScribe'],
  [AWSService.Honeycode, 'Amazon Honeycode'],
  [AWSService.IAM, 'AWS Identity and Access Management'],
  [AWSService.IAMIdentityCenter, 'IAM Identity Center'],
  [AWSService.IAMAccessAnalyzer, 'AWS IAM Access Analyzer'],
  [AWSService.ImageBuilder, 'EC2 Image Builder'],
  [AWSService.ImportExport, 'AWS Import/Export'],
  [AWSService.Inspector, 'Amazon Inspector'],
  [AWSService.IoT, 'AWS IoT Core'],
  [AWSService.IoTAnalytics, 'AWS IoT Analytics (deprecated)'],
  [AWSService.IoTCoreForLoRaWan, 'AWS IoT Core for LoRaWAN'],
  [AWSService.IoTDeviceDefender, 'AWS IoT Device Defender'],
  [AWSService.IoTDeviceManagement, 'AWS IoT Device Management'],
  [AWSService.IoTEvents, 'AWS IoT Events'],
  [AWSService.IoTFleetWise, 'AWS IoT FleetWise'],
  [AWSService.IoTGreengrass, 'AWS IoT Greengrass'],
  [AWSService.IoTRoboRunner, 'AWS IoT RoboRunner'],
  [AWSService.IoTSiteWise, 'AWS IoT SiteWise'],
  [AWSService.IoTThingsGraph, 'AWS IoT Things Graph (deprecated)'],
  [AWSService.IoTTwinMaker, 'AWS IoT TwinMaker'],
  [AWSService.IoTWireless, 'AWS IoT Wireless'],
  [AWSService.IoT1Click, 'AWS IoT 1-Click (deprecated)'],
  [AWSService.IQ, 'AWS IQ'],
  [AWSService.IVS, 'Amazon IVS'],
  [AWSService.KeyManagementService, 'AWS KMS'],
  [AWSService.Kendra, 'Amazon Kendra'],
  [AWSService.Keyspaces, 'Amazon Keyspaces'],
  [AWSService.Kinesis, 'Amazon Kinesis'],
  [AWSService.KinesisDataAnalytics, 'Amazon Kinesis Data Analytics (deprecated)'],
  [AWSService.KinesisDataFirehose, 'Amazon Data Firehose'], // rebranded
  [AWSService.DataFirehose, 'Amazon Data Firehose'],
  [AWSService.KinesisDataStreams, 'Amazon Kinesis Data Streams'],
  [AWSService.KinesisVideoStreams, 'Amazon Kinesis Video Streams'],
  [AWSService.Kiro, 'Kiro'],
  [AWSService.LakeFormation, 'AWS Lake Formation'],
  [AWSService.Lambda, 'AWS Lambda'],
  [AWSService.LaunchWizard, 'AWS Launch Wizard'],
  [AWSService.Lex, 'Amazon Lex'],
  [AWSService.LicenseManager, 'AWS License Manager'],
  [AWSService.LightSail, 'Amazon Lightsail'],
  [AWSService.LocalZones, 'AWS Local Zones'],
  [AWSService.LocationService, 'Amazon Location Service'],
  [AWSService.LookoutForEquipment, 'Amazon Lookout for Equipment'],
  [AWSService.LookoutForMetrics, 'Amazon Lookout for Metrics (deprecated)'],
  [AWSService.LookoutForVision, 'Amazon Lookout for Vision (deprecated)'],
  [AWSService.Lumberyard, 'Amazon Lumberyard (deprecated)'],
  [AWSService.MachineLearning, 'Amazon ML (deprecated)'],
  [AWSService.Macie, 'Amazon Macie'],
  [AWSService.MainframeModernization, 'AWS Mainframe Modernization'],
  [AWSService.ManagedBlockchain, 'Amazon Managed Blockchain'],
  [AWSService.ManagedServiceForGrafana, 'Amazon Managed Grafana'],
  [AWSService.ManagedServiceForFlink, 'Amazon Managed Service for Apache Flink'],
  [AWSService.ManagedServices, 'AWS Managed Services'], // duplicate
  [AWSService.ManagementConsole, 'AWS Management Console'],
  [AWSService.ManagementPortalForVCenter, 'AWS Management Portal for vCenter'],
  [AWSService.Marketplace, 'AWS Marketplace'],
  [AWSService.MechanicalTurk, 'Amazon Mechanical Turk'],
  [AWSService.MediaConnect, 'AWS Elemental MediaConnect'],
  [AWSService.MediaConvert, 'AWS Elemental MediaConvert'],
  [AWSService.MediaLive, 'AWS Elemental MediaLive'],
  [AWSService.MediaPackage, 'AWS Elemental MediaPackage'],
  [AWSService.MediaStore, 'AWS Elemental MediaStore (deprecated)'],
  [AWSService.MediaTailor, 'AWS Elemental MediaTailor'],
  [AWSService.ElementalOnPremises, 'AWS Elemental appliances and software'],
  [AWSService.MemoryDB, 'Amazon MemoryDB'],
  [AWSService.MicroserviceExtractorForNet, 'AWS Microservice Extractor for .NET'],
  [AWSService.MigrationHub, 'AWS Migration Hub'],
  [AWSService.MigrationEvaluator, 'Migration Evaluator'],
  [AWSService.MobileAnalytics, 'Amazon Mobile Analytics'],
  [AWSService.MobileHub, 'AWS Mobile Hub'],
  [AWSService.Monitron, 'Amazon Monitron'],
  [AWSService.MQ, 'Amazon MQ'],
  [AWSService.MSK, 'Amazon MSK'],
  [AWSService.MWAA, 'Amazon MWAA'],
  [AWSService.MxNet, 'Apache MXNet on AWS'],
  [AWSService.Neptune, 'Amazon Neptune'],
  [AWSService.NetworkFirewall, 'AWS Network Firewall'],
  [AWSService.NimbleStudio, 'Amazon Nimble Studio (deprecated)'],
  [AWSService.Nova, 'Amazon Nova'],
  [AWSService.NovaAct, 'Amazon Nova Act'],
  [AWSService.Omics, 'AWS HealthOmics'],
  [AWSService.OpenSearchService, 'Amazon OpenSearch Service'],
  [AWSService.OpsWorks, 'AWS OpsWorks (deprecated)'],
  [AWSService.OpsWorksForChefAutomate, 'AWS OpsWorks for Chef Automate'],
  [AWSService.OpsWorksStacks, 'AWS OpsWorks Stacks'],
  [AWSService.OracleDatabaseAWS, 'Oracle Database@AWS'],
  [AWSService.Organizations, 'AWS Organizations'],
  [AWSService.OutPosts, 'AWS Outposts'],
  [AWSService.Panorama, 'AWS Panorama'],
  [AWSService.ParallelCluster, 'AWS ParallelCluster'],
  [AWSService.ParallelComputingService, 'AWS PCS'],
  [AWSService.PaymentCryptography, 'AWS Payment Cryptography'],
  [AWSService.PersonalHealthDashboard, 'AWS Health Dashboard'], // rebranded to HealthDashboard
  [AWSService.HealthDashboard, 'AWS Health Dashboard'],
  [AWSService.Personalize, 'Amazon Personalize'],
  [AWSService.Pinpoint, 'Amazon Pinpoint'],
  [AWSService.Polly, 'Amazon Polly'],
  [AWSService.PrivateCa, 'AWS Private CA'],
  [AWSService.PrivateSG, 'AWS Private 5G (deprecated)'], // name update
  [AWSService.Private5G, 'AWS Private 5G (deprecated)'],
  [AWSService.Prometheus, 'Amazon Managed Service for Prometheus'],
  [AWSService.Proton, 'AWS Proton'],
  [AWSService.Q, 'Amazon Q'],
  [AWSService.QBusiness, 'Amazon Q Business'],
  [AWSService.QDeveloper, 'Amazon Q Developer'],
  [AWSService.QLDB, 'Amazon QLDB (deprecated)'],
  [AWSService.QuickSight, 'Amazon Quick Sight'],
  [AWSService.QuickSuite, 'Amazon Quick Suite'],
  [AWSService.RDS, 'Amazon RDS'],
  [AWSService.ReachabilityAnalyzer, 'VPC Reachability Analyzer'],
  [AWSService.RedShift, 'Amazon Redshift'],
  [AWSService.Rekognition, 'Amazon Rekognition'],
  [AWSService.RepostPrivate, 'AWS re:Post Private'],
  [AWSService.ResilienceHub, 'AWS Resilience Hub'],
  [AWSService.ResourceExplorer, 'AWS Resource Explorer'],
  [AWSService.ResourceGroups, 'AWS Resource Groups'],
  [AWSService.ResourceAccessManager, 'AWS RAM'],
  [AWSService.RoboMaker, 'AWS RoboMaker (deprecated)'],
  [AWSService.ROSA, 'Red Hat OpenShift Service on AWS'],
  [AWSService.Route53, 'Amazon Route 53'],
  [AWSService.RTBFabric, 'AWS RTB Fabric'],
  [AWSService.SageMaker, 'Amazon SageMaker'],
  [AWSService.SavingsPlans, 'AWS Savings Plans'],
  [AWSService.S3, 'Amazon S3'],
  [AWSService.SAM, 'AWS SAM'],
  [AWSService.SCT, 'AWS SCT'],
  [AWSService.SdkForAndroid, 'AWS Mobile SDK for Android'],
  [AWSService.SdkForC, 'AWS SDK for C++'],
  [AWSService.SdkForGo, 'AWS SDK for Go'],
  [AWSService.SdkForIOs, 'AWS Mobile SDK for iOS'],
  [AWSService.SdkForJava, 'AWS SDK for Java'],
  [AWSService.SdkForJavaScriptInTheBrowser, 'AWS SDK for JavaScript'],
  [AWSService.SdkForJavaScriptInNodeJs, 'AWS SDK for JavaScript in Node.js (deprecated)'],
  [AWSService.SdkForNet, 'AWS SDK for .NET'],
  [AWSService.SdkForNetAndXamarin, 'AWS Mobile SDK for Xamarin (deprecated)'],
  [AWSService.SdkForPhp, 'AWS SDK for PHP'],
  [AWSService.SdkForPythonBoto3, 'AWS SDK for Python (Boto3)'],
  [AWSService.SdkForRuby, 'AWS SDK for Ruby'],
  [AWSService.SdkForSapAbap, 'AWS SDK for SAP ABAP'],
  [AWSService.SdkForUnity, 'AWS Mobile SDK for Unity'],
  [AWSService.SecretsManager, 'AWS Secrets Manager'],
  [AWSService.SecurityAgent, 'AWS Security Agent'],
  [AWSService.SecurityHub, 'AWS Security Hub'],
  [AWSService.SecurityHubCSPM, 'AWS Security Hub CSPM'],
  [AWSService.SecurityIncidentResponse, 'AWS Security Incident Response'],
  [AWSService.SecurityLake, 'Amazon Security Lake'],
  [AWSService.ServelessApplicationRepository, 'AWS Serverless Application Repository'],
  [AWSService.ServerMigrationService, 'AWS SMS (deprecated)'],
  [AWSService.ServiceCatalog, 'AWS Service Catalog'],
  [AWSService.ServiceHealthDashboard, 'AWS Service Health Dashboard (deprecated)'],
  [AWSService.ServiceQuotas, 'Service Quotas'],
  [AWSService.SES, 'Amazon SES'],
  [AWSService.Shield, 'AWS Shield'],
  [AWSService.Signer, 'AWS Signer'],
  [AWSService.Silk, 'Amazon Silk'],
  [AWSService.SimpleAD, 'Simple AD (deprecated)'],
  [AWSService.SimpleDb, 'Amazon SimpleDB'],
  [AWSService.SimSpaceWeaver, 'AWS SimSpace Weaver'],
  [AWSService.SiteToSiteVpn, 'AWS Site-to-Site VPN'],
  [AWSService.SnowDeviceManagement, 'AWS Snow Device Management'],
  [AWSService.SnowFamily, 'AWS Snow Family'],
  [AWSService.Snowball, 'AWS Snowball'],
  [AWSService.SnowballEdge, 'AWS Snowball Edge'],
  [AWSService.Snowcone, 'AWS Snowcone (deprecated)'],
  [AWSService.Snowmobile, 'AWS Snowmobile (deprecated)'],
  [AWSService.SNS, 'Amazon SNS'],
  [AWSService.SQS, 'Amazon SQS'],
  [AWSService.SSO, 'AWS IAM Identity Center'], // duplicate
  [AWSService.StepFunctions, 'AWS Step Functions'],
  [AWSService.StorageGateway, 'AWS Storage Gateway'],
  [AWSService.STS, 'AWS STS'],
  [AWSService.Sumerian, 'Amazon Sumerian (deprecated)'],
  [AWSService.SupplyChain, 'AWS Supply Chain'],
  [AWSService.Support, 'AWS Support'],
  [AWSService.SWF, 'Amazon SWF'],
  [AWSService.SystemsManager, 'AWS Systems Manager'],
  [AWSService.TelcoNetworkBuilder, 'AWS Telco Network Builder'],
  [AWSService.Textract, 'Amazon Textract'],
  [AWSService.Thinkbox, 'AWS Thinkbox'],
  [AWSService.Timestream, 'Amazon Timestream'],
  [AWSService.ToolkitForEclipse, 'AWS Toolkit for Eclipse'],
  [AWSService.ToolkitForJetBrains, 'AWS Toolkit for JetBrains'],
  [AWSService.ToolkitForVisualStudio, 'AWS Toolkit for Visual Studio'],
  [AWSService.ToolkitForVsCode, 'AWS Toolkit for Visual Studio Code'],
  [AWSService.ToolkitForAzureDevOps, 'AWS Toolkit for Microsoft Azure DevOps'],
  [AWSService.ToolsForPowerShell, 'AWS Tools for PowerShell'],
  [AWSService.Transcribe, 'Amazon Transcribe'],
  [AWSService.TransferFamily, 'AWS Transfer Family'],
  [AWSService.Transform, 'AWS Transform'],
  [AWSService.Transitgateway, 'AWS Transit Gateway'],
  [AWSService.Translate, 'Amazon Translate'],
  [AWSService.TrustedAdvisor, 'AWS Trusted Advisor'],
  [AWSService.VMwareCloudOnAWS, 'VMware Cloud on AWS (deprecated)'],
  [AWSService.UserNotifications, 'AWS User Notifications'],
  [AWSService.VerifiedAccess, 'AWS Verified Access'],
  [AWSService.VPC, 'Amazon VPC'],
  [AWSService.VPCLattice, 'Amazon VPC Lattice'],
  [AWSService.WAF, 'AWS WAF'],
  [AWSService.WAM, 'Amazon WAM (deprecated)'],
  [AWSService.Wavelength, 'AWS Wavelength'],
  [AWSService.WellArchitected, 'AWS Well-Architected (deprecated)'],
  [AWSService.WellArchitectedTool, 'AWS Well-Architected Tool'],
  [AWSService.Wickr, 'AWS Wickr'],
  [AWSService.WorkDocs, 'Amazon WorkDocs'],
  [AWSService.WorkLink, 'Amazon WorkLink (deprecated)'],
  [AWSService.WorkMail, 'Amazon WorkMail'],
  [AWSService.WorkSpaces, 'Amazon WorkSpaces'],
  [AWSService.WorkSpacesWeb, 'Amazon WorkSpaces Secure Browser'], // rebranded
  [AWSService.WorkSpacesSecureBrowser, 'Amazon WorkSpaces Secure Browser'],
  [AWSService.WorkSpacesApplicationManager, 'Amazon WAM (deprecated)'],
  [AWSService.WorkSpacesCore, 'Amazon WorkSpaces Core'],
  [AWSService.WorkSpacesThinClient, 'Amazon WorkSpaces Thin Client'],
  [AWSService.XRay, 'AWS X-Ray'],
]);

export const patternCategoryDisplayNames = new Map<PatternCategory, string>([
  [PatternCategory.Blockchain, 'Blockchain'],
  [PatternCategory.BusinessSoftware, 'Business Software'],
  [PatternCategory.DataAndAnalytics, 'Data and Analytics'],
  [PatternCategory.DesktopAndAppStreaming, 'Desktop and App Streaming'],
  [PatternCategory.DeveloperTools, 'Developer Tools'],
  [PatternCategory.EnterpriseApplications, 'Enterprise Applications'],
  [PatternCategory.EnterpriseOperations, 'Enterprise Operations'],
  [PatternCategory.IoT, 'IoT'],
  [PatternCategory.PlatformMigration, 'Platform Migration'],
  [PatternCategory.SoftwareInfrastructure, 'Software Infrastructure'],
  [PatternCategory.Solutions, 'Solutions'],
  [PatternCategory.IndependentSoftwareVendor, 'Independent Software Vendor'],
  [PatternCategory.LifeCycle, 'Life Cycle'],
  [PatternCategory.PatternAccreditation, 'Pattern Accreditation'],
  [PatternCategory.PatternProvenance, 'Pattern Provenance'],
  [PatternCategory.PatternType, 'Pattern Type'],
  [PatternCategory.MachineLearning, 'Machine Learning'],
]);

export const userTitleDisplayNames = new Map<UserTitle, string>([
  [UserTitle.Admin, 'Administrator'],
  [UserTitle.Techwriter, 'Tech writer'],
  [UserTitle.Publisher, 'Publisher'],
  [UserTitle.Reviewer, 'Reviewer'],
  [UserTitle.PeerReviewer, 'SME reviewer'],
  [UserTitle.CoAuthor, 'Co-author'],
  [UserTitle.Author, 'Author'],
  [UserTitle.BarRaiser, 'Bar Raiser'],
]);

export const yearsOfExperienceDisplayNames = new Map<YearsOfExperience, string>([
  [YearsOfExperience.LessThanOneYear, 'Less than 1 year'],
  [YearsOfExperience.OneYear, '1 year'],
  [YearsOfExperience.TwoYears, '2 years'],
  [YearsOfExperience.ThreeYears, '3 years'],
  [YearsOfExperience.FourYears, '4 years'],
  [YearsOfExperience.FiveYears, '5 years'],
  [YearsOfExperience.SixToTenYears, '6 - 10 years'],
  [YearsOfExperience.MoreThanTenYears, 'More than 10 years'],
]);

export const hrefDisplayNames = new Map<ContentNodeType, string>([
  [ContentNodeType.Document, 'Pathslug'],
  [ContentNodeType.Page, 'Page ID'],
  [ContentNodeType.Section, 'Section ID'],
]);

export interface PublicationFeedbackType {
  readonly name: string;
  readonly severity: PublicationFeedbackTypeSeverity;
}

export type PublicationFeedbackTypeSeverity = 'NA' | 'Low' | 'Medium' | 'High';

export const publicationFeedbackTypeDisplayName = new Map<
  PublicationIssueType,
  PublicationFeedbackType
>([
  [PublicationIssueType.Grammar, { name: 'Grammar or spelling', severity: 'Low' }],
  [PublicationIssueType.Link, { name: 'Broken link', severity: 'Medium' }],
  [PublicationIssueType.Outdated, { name: 'Inaccurate information', severity: 'High' }],
  [PublicationIssueType.Display, { name: 'Display and design', severity: 'Low' }],
  [PublicationIssueType.Incomplete, { name: 'Incomplete information', severity: 'High' }],
  [PublicationIssueType.Improvement, { name: 'Suggested improvement', severity: 'Medium' }],
  [
    PublicationIssueType.BarRaiserUpdateRequest,
    { name: 'APG Bar Raiser Update Request', severity: 'High' },
  ],
  [PublicationIssueType.PassiveVoice, { name: 'Passive voice', severity: 'Low' }],
  [
    PublicationIssueType.UnprofessionalLanguage,
    { name: 'Unprofessional language', severity: 'Low' },
  ],
  [PublicationIssueType.RedundantContent, { name: 'Redundant content', severity: 'Low' }],
  [PublicationIssueType.InconsistentTone, { name: 'Inconsistent tone', severity: 'Low' }],
  [PublicationIssueType.Superlatives, { name: 'Superlatives', severity: 'Low' }],
  [PublicationIssueType.ServiceNaming, { name: 'Service naming', severity: 'Low' }],
  [PublicationIssueType.MissingPrerequisite, { name: 'Missing prerequisite', severity: 'Low' }],
  [PublicationIssueType.NonInclusiveTerms, { name: 'Non-inclusive terms', severity: 'Low' }],
]);

/** Issue type names listed in an update request title before the rest are counted as "N more". */
const MAX_LISTED_ISSUE_TYPES = 2;

/**
 * Title of an update request: the names of `issueType` and `additionalIssueTypes`,
 * e.g. "A", "A and B", or "A, B and 3 more". `undefined` when no type has a display name.
 */
export const getUpdateRequestTitle = (
  data: PublicationFeedbackData | undefined,
): string | undefined => {
  if (!data) return undefined;
  const issueTypes = new Set([data.issueType, ...(data.additionalIssueTypes ?? [])]);
  const names = [...issueTypes]
    .map((issueType) => publicationFeedbackTypeDisplayName.get(issueType)?.name)
    .filter((name): name is string => name !== undefined);
  if (names.length === 0) return undefined;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  const listed = names.slice(0, MAX_LISTED_ISSUE_TYPES).join(', ');
  return `${listed} and ${names.length - MAX_LISTED_ISSUE_TYPES} more`;
};

export interface PublicationFeedbackStateInfo {
  readonly name: string;
  readonly color: 'green' | 'blue' | 'grey';
}

export const publicationFeedbackStateDisplayName = new Map<
  PublicationFeedbackState,
  PublicationFeedbackStateInfo
>([
  [PublicationFeedbackState.CREATED, { name: 'Assigned', color: 'grey' }],
  [PublicationFeedbackState.PENDING, { name: 'In progress', color: 'blue' }],
  [PublicationFeedbackState.RESOLVED, { name: 'Resolved', color: 'green' }],
  [PublicationFeedbackState.NO_CHANGE_NEEDED, { name: 'No change needed', color: 'grey' }],
]);

export const isFeedbackClosed = (state: PublicationFeedbackState): boolean => {
  return (
    state === PublicationFeedbackState.RESOLVED ||
    state === PublicationFeedbackState.NO_CHANGE_NEEDED
  );
};

export const workflowTagDisplayName = new Map<WorkflowTag, string>([
  [WorkflowTag.INFRINGED, 'Infringed'],
  [WorkflowTag.MOVED_TO_SUGGESTIONS, 'Moved to suggestions'],
  [WorkflowTag.NEEDS_IAM_REVIEW, 'Needs IAM review'],
  [WorkflowTag.TEST, 'Test'],
  [WorkflowTag.UPDATE_IN_PROGRESS, 'Update in progress'],
  [WorkflowTag.WAITING_FOR_UPDATE, 'Waiting for update'],
  [WorkflowTag.CUSTOM_XML, 'Custom XML'],
  [WorkflowTag.EKS_PILOT, 'EKS pilot'],
  [WorkflowTag.SOLUTIONS_PILOT, 'Solutions pilot'],
  [WorkflowTag.AUTOMATED_PREAPPROVAL, 'Automated pre-approval'],
]);

export const techWriterStatusDisplayNames = new Map<TechWriterStatus, string>([
  [TechWriterStatus.ReadyForTechWriter, 'Ready for tech writer'],
  [TechWriterStatus.CheckAuthorAvailability, 'Checking author’s availability'],
  [TechWriterStatus.AuthorNotResponding, 'Author not responding'],
  [TechWriterStatus.WaitingCodeRepoApproval, 'Waiting for code repo approval'],
  [TechWriterStatus.SentBackToAuthor, 'Sent back to author'],
  [TechWriterStatus.BackFromAuthor, 'Back from author'],
  [TechWriterStatus.InEdit, 'In edit'],
  [TechWriterStatus.InReview, 'In review'],
  [TechWriterStatus.InIntegReview, 'In integ review'],
  [TechWriterStatus.Complete, 'Complete'],
  [TechWriterStatus.Other, 'Other'],
]);

export const barRaiserStatusDisplayNames = new Map<BarRaiserStatus, string>([
  [BarRaiserStatus.BarRaiserReviewing, 'Bar raiser reviewing'],
  [BarRaiserStatus.NeedsNewBarRaiser, 'Needs new bar raiser'],
  [BarRaiserStatus.UnpublishingRecommended, 'Unpublishing recommended'],
]);

export const BR_APPROVED_VALUE = 'Approved';
export const BR_APPROVAL_PENDING_VALUE = 'ApprovalPending';

export const barRaiserReviewStatusDisplayNames = new Map<string, string>([
  [BR_APPROVED_VALUE, 'BR approved'],
  [BR_APPROVAL_PENDING_VALUE, 'BR approval pending'],
]);

export const contentPriorityDisplayNames = new Map<ContentPriority, string>([
  [ContentPriority.Low, '1 - Low'],
  [ContentPriority.Medium, '2 - Medium'],
  [ContentPriority.High, '3 - High'],
]);

export const responseOptionsDisplayNames = new Map<ContentQualityResponseOptions, string>([
  [ContentQualityResponseOptions.YES, 'Yes'],
  [ContentQualityResponseOptions.NO, 'No'],
  [ContentQualityResponseOptions.NA, 'N/A'],
]);

export const workflowEventTypeDisplayNames = new Map<WorkflowEventTypes, string>([
  [WorkflowEventTypes.APPROVE, 'Peer Approved'],
  [WorkflowEventTypes.DISAPPROVE, 'Peer Rejected'],
  [WorkflowEventTypes.REJECT, 'Publisher Rejected'],
  [WorkflowEventTypes.PUBLISH, 'Publisher Approved'],
  [WorkflowEventTypes.UNPUBLISH, 'Unpublished'],
  [WorkflowEventTypes.LOCK, 'Edit'],
  [WorkflowEventTypes.UNLOCK, 'Unlock'],
  [WorkflowEventTypes.SEND_FOR_REVIEW, 'Submitted for review'],
  [WorkflowEventTypes.ACCEPT_REVIEW, 'In review'],
  [WorkflowEventTypes.DECLINE_REVIEW, 'Declined review request'],
  [WorkflowEventTypes.RETRACT_REVIEW, 'Review retracted'],
  [WorkflowEventTypes.AUTO_REMOVE_REVIEWER, 'Peer reviewer auto-removed'],
  [WorkflowEventTypes.CHANGE_OWNER, 'Change owner'],
  [WorkflowEventTypes.FINALIZE, 'Tech Writer Approved'],
  [WorkflowEventTypes.DISTRIBUTE, 'Publish to public APG'],
  [WorkflowEventTypes.PUBLISH_TO_APG, 'Publish to public APG'],
  [WorkflowEventTypes.REVOKE, 'Revoked from public APG'],
  [WorkflowEventTypes.UNPUBLISH_FROM_APG, 'Revoke from public APG'],
  [WorkflowEventTypes.DEMOTE_AUDIENCE, 'Demoted audience'],
  [WorkflowEventTypes.EDIT_CONTENT_TYPE, 'Content type changed'],
  [WorkflowEventTypes.PREAUTHORING_APPROVE, 'Publisher approved'],
  [WorkflowEventTypes.PREAUTHORING_REJECT, 'Publisher sent back to author'],
  [WorkflowEventTypes.PREAUTHORING_NEED_MORE_INFO, 'Publisher requested more information'],
  [WorkflowEventTypes.NO_UPDATES_NEEDED, 'Bar raiser reviewed - no updates needed'],
  [WorkflowEventTypes.UPDATES_NEEDED, 'Bar raiser reviewed - author updates needed'],
  [WorkflowEventTypes.UNPUBLISH_RECOMMENDED, 'Bar raiser recommended unpublishing'],
  [WorkflowEventTypes.CONFIRM_UNPUBLISH, 'Bar raiser confirmed unpublishing'],
  [WorkflowEventTypes.PROPOSAL_APPROVE, 'Created draft'],
  [WorkflowEventTypes.START_QUALITY_CHECK, 'Quality check started'],
  [WorkflowEventTypes.SUBMIT_FOR_PUBLISHER_REVIEW, 'Bar raiser review requested'],
  [WorkflowEventTypes.CANCEL_QUALITY_CHECK, 'Quality check cancelled'],
  [WorkflowEventTypes.PUBLISHER_REVIEW_APPROVE, 'Bar raiser approved'],
  [WorkflowEventTypes.PUBLISHER_REVIEW_REJECT, 'Bar raiser rejected'],
  [WorkflowEventTypes.PUBLISHER_REVIEW_RESUBMIT, 'Resubmitted for publisher review'],
]);
